import { Card, Center, Flex, Heading, Td, Tr, IconButton } from "@chakra-ui/react";
import React, { useCallback, useState, useEffect } from "react";
import { BiArrowBack } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import store from "../../redux/store";
import { get_full_product_list } from "../../redux/productSlice";
import TableView from "../../components/TableView";
import { omcOptions } from "../../constant/data";
import CustomSingleInput from "../../components/CustomSingleInput";
import CustomButton from "../../components/CustomButton";
import { baseUrl, formatIndianPrice } from "../../utils/utils";
import { get_vehicle_list } from "../../redux/vehiclSlice";
import { get_vendor_list } from "../../redux/vendorSlice";
import { get_purchase_list } from "../../redux/purchaseSlice";
import VehicleAutocomplete from "./VehicleAutoComplete";

// Updated validation schema
const AddProductSchema = Yup.object().shape({
  omc: Yup.string().required("OMC selection is required"),
  INV_no: Yup.string()
    .required("INV Number is required")
    .matches(
      /^[a-zA-Z0-9]+$/,
      "INV Number must contain only letters and numbers"
    ),
  vendor_id: Yup.string().required("Vendor ID is required"),
  INV_date: Yup.string().required("INV date is required"),
});

// Single product validation schema
const SingleProductSchema = Yup.object().shape({
  product: Yup.string().required("Product selection is required"),
  emptyReturn: Yup.string().required("Empty return is required"),
  unit_rate: Yup.number()
    .required("Unit rate is required")
    .min(0.01, "Unit rate must be greater than 0"),
  discount: Yup.number()
    .min(0, "Discount cannot be negative")
    .test(
      "discount-validation",
      "Discount cannot be greater than unit rate",
      function (value) {
        const { unit_rate } = this.parent;
        return !value || !unit_rate || value <= unit_rate;
      }
    ),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(0.01, "Quantity must be greater than 0"),
  vehicle_no: Yup.string().required("Vehicle number is required"),
});

const AddPurchase = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showInputRow, setShowInputRow] = useState(true);
  const { full_product_list } = useSelector((state) => state.products);
  const { vendor_list } = useSelector((state) => state.vendors);
  const { vehicle_list, total_vehicle_count } = useSelector(
    (state) => state.vehicles
  );
  const [inventory_list, setInventory_list] = useState([]);
  const [loading, setLoading] = useState(false);

  const [single_product, setSingle_product] = useState({});
  const [products_array, setProducts_array] = useState([]);

  const [singleProductErrors, setSingleProductErrors] = useState({});
  const [isValidatingSingleProduct, setIsValidatingSingleProduct] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const vendorList = vendor_list?.map((val, ind) => ({
    value: val?.id,
    label: val.legalname,
  }));
  const product_list = full_product_list?.map((val, ind) => ({
    value: val?.product_code,
    label: val.description + "-" + val?.category + "-" + val?.type,
    ...val,
  }));

  const filtered_product_list = (type) =>
    product_list?.filter((v) => v.type == type);

  const initialValues = {
    omc: "",
    INV_amount: "",
    INV_date: "",
    vendor_id: "",
    INV_no: "",
  };

  useEffect(() => {
    store.dispatch(get_full_product_list());
    store.dispatch(get_vendor_list({ page: 1, rows: 10000 }));
    store.dispatch(get_vehicle_list({ page: 1, rows: 100 })); // ADD THIS LINE
  }, []);

  // Enhanced handleChangeProduct with validation
  const handleChangeProduct = async (key, value) => {
    const updatedProduct = { ...single_product, [key]: value };
    setSingle_product(updatedProduct);
    validateSingleProduct(updatedProduct);
  };

  // Validation function for single product
  const validateSingleProduct = async (productData) => {
    try {
      await SingleProductSchema.validate(productData, { abortEarly: false });
      setSingleProductErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      setSingleProductErrors(errors);
      return false;
    }
  };

  const handleRemoveInventory = (id) => {
    setProducts_array((prevList) => prevList.filter((item, ind) => ind !== id));
  };

  const clearSingleProduct = () => {
    setSingle_product({});
    setSingleProductErrors({});
  };

  const fetchGST = (omc, key, uuid) => {
    const product = filtered_product_list(omc);
    const gst_value = product?.filter((v) => v.value == uuid);

    setSingle_product((pre) => ({ ...pre, [key]: gst_value[0]?.gst }));
  };

  // Enhanced onAdd with validation
// Find the onAdd function and update it (around line 115)
const onAdd = async (values, { resetForm, setSubmitting }) => {
  if (isSubmittingForm) return;

  try {
    setSubmitting(true);
    setIsSubmittingForm(true);
    setIsValidatingSingleProduct(true);

    // Validate single product before adding
    const isValid = await validateSingleProduct(single_product);

    if (!isValid) {
      toast.error("Please fix the errors in the product form", { toastId: "product-validation-error" });
      return;
    }

    // Check for duplicate products
    const isDuplicate = products_array.some(
      (product) => product.product === single_product.product
    );

    if (isDuplicate) {
      toast.error("This product is already added", { toastId: "duplicate-product" });
      return;
    }

    // Calculate amounts before adding
    const calculatedProduct = {
      ...single_product,
      taxable_amount:
        (parseFloat(single_product.unit_rate) - (parseFloat(single_product.discount) || 0)) *
        parseFloat(single_product.quantity),
      gst_amount:
        ((parseFloat(single_product.unit_rate) - (parseFloat(single_product.discount) || 0)) *
          parseFloat(single_product.quantity) *
          parseFloat(single_product.gst)) /
        100,
    };

    // Add product to array
    setProducts_array((prevList) => [...prevList, calculatedProduct]);
    
    // Clear ONLY the product input fields
    setSingle_product({});
    setSingleProductErrors({});
    
    // Hide input row
    setShowInputRow(false);
    
    // **REMOVED: Don't reset the main form here**
    // resetForm(); // <-- REMOVE THIS LINE
    
    toast.success("Product added successfully", { toastId: "product-added" });
  } catch (error) {
    console.error("Error adding product:", error);
    toast.error("Error adding product", { toastId: "add-product-error" });
  } finally {
    setSubmitting(false);
    setIsSubmittingForm(false);
    setIsValidatingSingleProduct(false);
  }
};

  // Enhanced calculate_amount function
const calculate_amount = () => {
  // ADDED: Prevent multiple executions
  if (isSubmittingForm || isValidatingSingleProduct) return;

  if (
    !single_product?.unit_rate ||
    !single_product?.quantity ||
    !single_product.gst
  ) {
    toast.error("Please enter gst, unit rate, discount and quantity first", { toastId: "calculate-validation" });
    return;
  }

  const unit_rate = parseFloat(single_product.unit_rate) || 0;
  const discount = parseFloat(single_product.discount) || 0;
  const quantity = parseFloat(single_product.quantity) || 0;
  const gst = parseFloat(single_product.gst) || 0;

  if (discount > unit_rate) {
    toast.error("Discount cannot be greater than unit rate", { toastId: "discount-validation" });
    return;
  }

  const taxable_amount = (unit_rate - discount) * quantity;
  const gst_amount = (taxable_amount * gst) / 100;

  const updatedProduct = {
    ...single_product,
    taxable_amount: taxable_amount,
    gst_amount: gst_amount,
  };
  setSingle_product(updatedProduct);
};

  const calculateTotals = () => {
    const totalTaxable = products_array?.reduce(
      (sum, item) => sum + (Number(item.taxable_amount) || 0),
      0
    );

    const totalGST = products_array?.reduce(
      (sum, item) => sum + (Number(item.gst_amount) || 0),
      0
    );

    return (totalTaxable + totalGST).toFixed(2);
  };

 // Find the handleSubmit function and update it (around line 190)
const handleSubmit = useCallback(
  async (values) => {
    if (isSubmittingForm) return;

    try {
      setLoading(true);
      setIsSubmittingForm(true);

      // NEW: Check if there's data in single_product that hasn't been added yet
      const hasUnaddedProduct = single_product?.product && 
                                single_product?.unit_rate && 
                                single_product?.quantity;
      
      let finalProductsArray = [...products_array];

      // NEW: If there's an unadded product, validate and add it to the array
      if (hasUnaddedProduct) {
        const isValid = await validateSingleProduct(single_product);
        
        if (!isValid) {
          toast.error("Please fix errors in the product form or clear it before saving", { toastId: "validation-before-save" });
          setLoading(false);
          setIsSubmittingForm(false);
          return;
        }

        // Calculate and add the product
        const calculatedProduct = {
          ...single_product,
          taxable_amount:
            (parseFloat(single_product.unit_rate) - (parseFloat(single_product.discount) || 0)) *
            parseFloat(single_product.quantity),
          gst_amount:
            ((parseFloat(single_product.unit_rate) - (parseFloat(single_product.discount) || 0)) *
              parseFloat(single_product.quantity) *
              parseFloat(single_product.gst)) /
            100,
        };
        
        finalProductsArray.push(calculatedProduct);
      }

      // Check if there are products to save
      if (finalProductsArray.length === 0) {
        toast.error("Please add at least one product", { toastId: "no-products" });
        setLoading(false);
        setIsSubmittingForm(false);
        return;
      }

      const formData = new FormData();

      formData.append("action", "create");
      formData.append("type", values?.omc);
      formData.append("inv_no", values?.INV_no);
      formData.append("inv_date", values?.INV_date);
      formData.append("vendor_id", values?.vendor_id);
      
      // Calculate total with finalProductsArray instead of products_array
      const totalTaxable = finalProductsArray?.reduce(
        (sum, item) => sum + (Number(item.taxable_amount) || 0),
        0
      );
      const totalGST = finalProductsArray?.reduce(
        (sum, item) => sum + (Number(item.gst_amount) || 0),
        0
      );
      formData.append("total_amount", (totalTaxable + totalGST).toFixed(2));

      finalProductsArray?.forEach((v) => {
        formData.append("uuid[]", v.product);
        formData.append("hsn[]", v.p_detail?.hsn_no);
        formData.append("gst[]", v.gst);
        formData.append("unit[]", v.p_detail?.uom);
        formData.append("unit_price[]", v.unit_rate);
        formData.append("discount[]", v.discount);
        formData.append("gst_amount[]", v.gst_amount);
        formData.append("category[]", v.p_detail?.category);
        formData.append("empty_returned[]", v.emptyReturn);
        formData.append("qty[]", v.quantity);
        formData.append("vehicle_no[]", v.vehicle_no);
      });

      const response = await fetch(baseUrl + "purchase.php", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();

    // In the handleSubmit function, after successful save, add:
if (response.ok && (result.success || result.status === "success")) {
  toast.success(result?.message, { toastId: "save-success" });
  store.dispatch(get_purchase_list({ page: 1, rows: 5 }));
  
  // Clear all form data before navigation
  setProducts_array([]);
  clearSingleProduct();
  setShowInputRow(true);
  
  navigate(-1);
} else {
        toast.error(result?.message, { toastId: "save-error" });
        throw new Error(
          result.message || result.error || "An error occurred"
        );
      }
    } catch (error) {
      console.error("Inventory submission error:", error);

      if (
        error.message.toLowerCase().includes("duplicate") ||
        error.message.toLowerCase().includes("already exists")
      ) {
        toast.error("Some inventory items already exist", { toastId: "duplicate-error" });
      } else if (error.message.toLowerCase().includes("unauthorized")) {
        toast.error("Session expired. Please login again.", { toastId: "auth-error" });
        navigate("/login");
      } else {
        const errorMessage = "Failed to add inventory. Please try again.";
        toast.error(error.message || errorMessage, { toastId: "submit-error" });
      }
    } finally {
      setLoading(false);
      setIsSubmittingForm(false);
    }
  },
  [navigate, inventory_list, setInventory_list, id, products_array, isSubmittingForm, single_product] // Add single_product to dependencies
);

  return (
    <>
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
      >
        <Heading fontSize={"xl"}>{"Add Purchase"}</Heading>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>

      {inventory_list?.length >= 4 || (
        <Formik
          key={"add"}
          initialValues={initialValues}
          validationSchema={AddProductSchema}
          onSubmit={onAdd}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            isValid,
            dirty,
            submitForm,
          }) => (
            <Form>
              <Card padding={5}>
                <CustomInput
                  label1={"OMC Plant"}
                  value1={values.omc}
                  onChange1={(v) => {
                    setFieldValue("omc", v.target.value);
                    setSingle_product({});
                  }}
                  onBlur1={handleBlur("omc")}
                  select1={true}
                  options1={omcOptions}
                  placeholder1={"Select OMC"}
                  error1={touched.omc && errors.omc}
                  required1={true}
                  label2={"INV No."}
                  value2={values.INV_no}
                  onChange2={handleChange("INV_no")}
                  onBlur2={handleBlur("INV_no")}
                  placeholder2={"Enter INV Number"}
                  error2={touched.INV_no && errors.INV_no}
                  required2={true}
                />

                <CustomInput
                  select1
                  label1={"Vendor ID"}
                  value1={values.vendor_id}
                  onChange1={handleChange("vendor_id")}
                  options1={vendorList}
                  onBlur1={handleBlur("vendor_id")}
                  placeholder1={"Enter Vendor ID"}
                  error1={touched.vendor_id && errors.vendor_id}
                  required1={true}
                  label2={"INV Date"}
                  value2={values.INV_date}
                  onChange2={handleChange("INV_date")}
                  onBlur2={handleBlur("INV_date")}
                  placeholder2={"Enter current stock"}
                  error2={touched.INV_date && errors.INV_date}
                  required2={true}
                  type2={"date"}
                />
                <CustomInput
                  disabled1
                  label1={"INV Amount"}
                  value1={calculateTotals() || 0}
                  placeholder1={"INV Amount"}
                />

                <TableView
                  headData={[
                    "product_name",
                    "GST%",
                    "Empty Return",
                    "Unit Rate",
                    "discount",
                    "Quantity",
                    "Taxable Amount",
                    "GST Amount",
                    "vehicle no",
                    "Action",
                  ]}
                  body={
                    <>
                      {products_array?.map((v, i) => (
                        <Tr key={i}>
                          <Td>{v?.p_detail?.description}</Td>
                          <Td>{v.gst}</Td>
                          <Td>{v.emptyReturn}</Td>
                          <Td>{v.unit_rate}</Td>
                          <Td>{formatIndianPrice(v.discount)}</Td>
                          <Td>{v.quantity}</Td>
                          <Td>{formatIndianPrice(v.taxable_amount)}</Td>
                          <Td>{formatIndianPrice(v.gst_amount)}</Td>
                          <Td>{v.vehicle_no}</Td> 
                          <Td>
                            <IconButton
                              icon={<MdDelete />}
                              colorScheme="red"
                              size="sm"
                              onClick={() => handleRemoveInventory(i)}
                              aria-label="Delete product"
                            />
                          </Td>
                        </Tr>
                      ))}
 {(products_array.length === 0 || showInputRow) && (
                      <Tr>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"200px"}
                            placeholder={"Product"}
                            select
                            disabled={!values.omc}
                            options={filtered_product_list(values.omc)}
                            value={single_product?.product || ""}
                            onChange={(e) => {
                              const selectedValue = e.target.value;

                              const p_detail = filtered_product_list(
                                values.omc
                              )?.find((item) => item.uuid === selectedValue);

                              setSingle_product((prev) => ({
                                ...prev,
                                product: selectedValue,
                                p_detail: p_detail,
                              }));
                              fetchGST(values.omc, "gst", selectedValue);
                            }}
                            error={singleProductErrors.product}
                          />
                        </Td>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"100px"}
                            placeholder={"GST"}
                            disabled
                            value={single_product?.gst || ""}
                          />
                        </Td>

                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"200px"}
                            placeholder={"Empty Return"}
                            value={single_product?.emptyReturn || ""}
                            onChange={(v) => {
                              const value = v.target.value;
                              if (/^\d*$/.test(value)) {
                                handleChangeProduct("emptyReturn", value);
                              }
                            }}
                            error={singleProductErrors.emptyReturn || ""}
                          />
                        </Td>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"120px"}
                            placeholder={"Unit Rate"}
                            value={single_product?.unit_rate || ""}
                            onChange={(v) => {
                              const value = v.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                handleChangeProduct("unit_rate", value);
                              }
                            }}
                            error={singleProductErrors.unit_rate}
                          />
                        </Td>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"120px"}
                            placeholder={"Discount"}
                            value={single_product?.discount || ""}
                            onChange={(v) => {
                              const value = v.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                handleChangeProduct("discount", value);
                              }
                            }}
                            error={singleProductErrors.discount}
                          />
                        </Td>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"100px"}
                            placeholder={"Quantity"}
                            value={single_product?.quantity || ""}
                            onChange={(v) => {
                              const value = v.target.value;
                              if (/^\d*\.?\d*$/.test(value)) {
                                handleChangeProduct("quantity", value);
                              }
                            }}
                            error={singleProductErrors.quantity}
                          />
                        </Td>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"170px"}
                            placeholder={"Taxable Amount"}
                            value={single_product?.taxable_amount || ""}
                            disabled
                          />
                        </Td>
                        <Td verticalAlign="top">
                          <CustomSingleInput
                            width={"150px"}
                            placeholder={"GST Amount"}
                            value={single_product?.gst_amount || ""}
                            disabled
                          />
                        </Td>
                        <Td verticalAlign="top">
  <VehicleAutocomplete
    width="150px"
    placeholder="Vehicle Number"
    value={single_product?.vehicle_no || ""}
    onChange={(value) => {
      handleChangeProduct("vehicle_no", value);
    }}
    onSelect={(vehicleNo) => {
      handleChangeProduct("vehicle_no", vehicleNo);
    }}
    vehicleList={vehicle_list}
    error={singleProductErrors.vehicle_no}
  />
</Td>
                        <Td verticalAlign="top">
                          <IconButton
                            icon={<MdDelete />}
                            colorScheme="gray"
                            size="sm"
                            onClick={clearSingleProduct}
                            aria-label="Clear form"
                            isDisabled={!single_product?.product}
                          />
                        </Td>
                      </Tr>
 )}
                    </>
                  }
                />

<Flex justifyContent={"flex-end"} marginBlock={5} gap={2}>
  {/* NEW: Show "Add New Product" button when input row is hidden and there are products */}
  {!showInputRow && products_array.length > 0 && (
  <CustomButton
    title={"Add New Product"}
    onClick={() => {
      clearSingleProduct(); // ADDED: Clear any residual data
      setShowInputRow(true);
    }}
  />
)}
  
  {/* Only show Add/Calculate buttons when input row is visible */}
  {(products_array.length === 0 || showInputRow) && (
    <>
     <CustomButton
  title={"Add"}
  onClick={() => {
    if (!isSubmittingForm && !isValidatingSingleProduct) {
      submitForm();
    }
  }}
  disabled={isSubmittingForm || isValidatingSingleProduct}
/>
    <CustomButton
  title={"Calculate"}
  onClick={() => {
    if (!isSubmittingForm && !isValidatingSingleProduct) {
      calculate_amount();
    }
  }}
  disabled={
    !single_product?.unit_rate ||
    !single_product?.quantity ||
    isValidatingSingleProduct ||
    isSubmittingForm
  }
/>
    </>
  )}
  
  <CustomButton
    title={"Save"}
    onClick={() => {
      if (isSubmittingForm) return;
      handleSubmit(values);
    }}
    disabled={isSubmittingForm || loading}
  />
</Flex>
              </Card>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddPurchase;